import React from 'react'
import { Modal } from 'antd'

interface ConfirmDestructiveActionOptions {
    /** Modal title (e.g., "Cancel Notification", "Archive Draft") */
    title: string
    /** Entity title to bold in the message (e.g., the notification title). Omitted if empty. */
    entityTitle?: string
    /** Message template. Use {entity} as placeholder for the bolded entity title. */
    message: string
    /** Confirm button text */
    okText: string
    /** Cancel button text. Default: "Keep" */
    cancelText?: string
    /** Called when the user confirms */
    onConfirm: () => void | Promise<void>
}

/**
 * Shows a confirmation modal for destructive actions (cancel, archive).
 * Bolds the entity title in the message for visual distinction.
 *
 * @example
 * confirmDestructiveAction({
 *     title: 'Cancel Notification',
 *     entityTitle: 'Breaking News',
 *     message: 'This notification{entity} will be cancelled. This cannot be undone.',
 *     okText: 'Cancel Notification',
 *     onConfirm: () => cancelNotification(id),
 * })
 */
export function confirmDestructiveAction({
    title,
    entityTitle,
    message,
    okText,
    cancelText = 'Keep',
    onConfirm,
}: ConfirmDestructiveActionOptions): void {
    const entityLabel = entityTitle?.trim() ? (
        <span>
            , <strong>{entityTitle}</strong>,
        </span>
    ) : null

    const content = (
        <span>
            {message.split('{entity}')[0]}
            {entityLabel}
            {message.split('{entity}')[1] ?? ''}
        </span>
    )

    Modal.confirm({
        title,
        content,
        okText,
        okButtonProps: { danger: true },
        cancelText,
        onOk: onConfirm,
    })
}
